import { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchImpactAnalysis,
  normalizeImpactAnalysisRequest,
  validateImpactAnalysisRequest,
  type ImpactAnalysisRequest,
  type ImpactAnalysisResult,
} from "../../api/impactAnalysis";

interface ImpactAnalysisLoadState {
  key: string | null;
  data: ImpactAnalysisResult | null;
  error: Error | null;
  loading: boolean;
}

export interface ImpactAnalysisState {
  data: ImpactAnalysisResult | null;
  error: Error | null;
  isLoading: boolean;
  refresh: () => void;
}

export function impactAnalysisRequestKey(request: ImpactAnalysisRequest): string {
  validateImpactAnalysisRequest(request);
  const normalized = normalizeImpactAnalysisRequest(request);
  return JSON.stringify([
    normalized.project_id,
    normalized.root_ref.entity_kind,
    normalized.root_ref.id,
    normalized.as_of ?? null,
    normalized.recorded_as_of ?? null,
    normalized.snapshot_token ?? null,
    normalized.max_depth ?? null,
    normalized.max_records ?? null,
    normalized.max_paths ?? null,
    normalized.max_path_steps ?? null,
  ]);
}

function aborted(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/** Load one exact, bounded cause-aware impact snapshot. */
export function useImpactAnalysis(request: ImpactAnalysisRequest | null): ImpactAnalysisState {
  const key = useMemo(() => request ? impactAnalysisRequestKey(request) : null, [
    request?.project_id,
    request?.root_ref.entity_kind,
    request?.root_ref.id,
    request?.as_of,
    request?.recorded_as_of,
    request?.snapshot_token,
    request?.max_depth,
    request?.max_records,
    request?.max_paths,
    request?.max_path_steps,
  ]);
  const [reload, setReload] = useState(0);
  const [state, setState] = useState<ImpactAnalysisLoadState>({
    key: null,
    data: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!request || !key) {
      setState({ key: null, data: null, error: null, loading: false });
      return;
    }
    const controller = new AbortController();
    let active = true;
    setState({ key, data: null, error: null, loading: true });
    void fetchImpactAnalysis(request, { signal: controller.signal }).then(
      (envelope) => {
        if (active) setState({ key, data: envelope.data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!active || aborted(error)) return;
        setState({
          key,
          data: null,
          error: error instanceof Error ? error : new Error("Could not load typed impact analysis."),
          loading: false,
        });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, reload]);

  const refresh = useCallback(() => {
    if (!key) return;
    setState({ key, data: null, error: null, loading: true });
    setReload((value) => value + 1);
  }, [key]);

  if (state.key !== key) {
    return { data: null, error: null, isLoading: key !== null, refresh };
  }
  return { data: state.data, error: state.error, isLoading: state.loading, refresh };
}
